import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useInRouterContext, useSearchParams } from 'react-router-dom';
import { api } from '../../utils/api';
import { statutoryBookName } from '../../utils/countryFeatures';
import { useReadOnlySeat } from '../../utils/readOnlySeat';
import { guardLeave } from '../../utils/unsaved';
import type { AwCtx } from './ctx';
import { Dialog, Loader, actBtn, emptyLine } from './dialog';
import { EntryDetailDialog } from './JournalBook';
import { ReverseReasonDialog } from './reverseReason';
import { SourceDocOpen } from './sourceDoc';
import { baht, btn, card, dmy, errBox, note, num, pillAmber, pillGreen, pillGrey, table, td, th } from './ui';

/**
 * COA-D2 / EXP-15 / PAY-18 — "what did this document do to the books" (owner 2026-10-19).
 *
 * GET /api/journal/entries-for/:kind/:id has answered that question since batch 3 and NOTHING in
 * the client called it: an accountant looking at an invoice could not see what it posted. This is
 * the caller — 没界面=没做, closed for the accounting half.
 *
 * ── Why this is its own file, and not a function in a pane ───────────────────────────────────────
 * THREE lists ask the same question — Sales' five document families (panes/Documents.tsx), the
 * supplier bills and the payables register (panes/Purchases.tsx) — and the server answers all of
 * them from ONE resolver (services/journalLinks.ts, whose own header says "one resolver, six
 * callers"). A copy of this panel per pane is how the three come to disagree about one document.
 * It is also why it is not inside a pane file: testDirtyClose.ts §2 and testFullscreenShell.ts §1
 * both count the `<Dialog` call sites of the four panes by name (Documents 1 · Banking 4 ·
 * Expenses 3 · Purchases 7) and account for each one, and a shared read-only window that belongs to
 * three of them is not one of those fifteen forms.
 *
 * ── The rules this panel is built on ────────────────────────────────────────────────────────────
 * ⛔ THE LINK IS THE SERVER'S. Nothing here matches a document number, a date or an amount against
 *    an entry. `kind` is the document's STORED doc_type, handed over untouched; the rows are
 *    whatever the resolver returned, in its order.
 * ⛔ NO `dirty`, AND NO INPUT OF ITS OWN. There is nothing in this subtree to lose — no input, no textarea,
 *    no select, no onChange — so the X, Esc and the backdrop have nothing to ask about, and a confirm
 *    on a read-only close is the false positive that teaches people to click warnings away. A
 *    posted entry is reversed, never edited. Since batch 11 R8a (design §3.5.3 / §3.6.4, W2-32) the
 *    panel's ONLY two writes are a draft's Post (api.jnlPost) and a posted entry's Reverse… — the
 *    latter through R2's ReverseReasonDialog, which owns the one input (the reason) and its own dirty
 *    guard, and calls api.jnlReverse. Each is drawn only when the server's own refusal for that row is
 *    null (postRefusal / reverseRefusal, read from the same functions the doors run first) AND the seat
 *    writes (useReadOnlySeat) — otherwise the refusal is said in words. A request the door would refuse
 *    pages the admins, so the panel never sends one.
 * ⛔ NO TOTAL ACROSS THE ROWS, and that is deliberate. A document that was posted and then reversed
 *    has TWO entries here whose effect is nil; adding their amounts would print double the
 *    document's value and call it the document's effect. Each row carries its own figure, the
 *    reversal is marked, and a line says why they must not be summed.
 * ⛔ THE DEBIT/CREDIT CHECK BELONGS TO THE VOUCHER. /entries-for returns one figure per entry (Σ dr)
 *    and no credit side, so this panel CANNOT state that an entry balances and does not pretend to.
 *    The entry number opens EntryDetailDialog (CompanyWorkspace/JournalBook.tsx — this round's other
 *    part), which reads GET /journal/entries/:entryId and shows the dr and cr totals and whether
 *    they agree. One detail view, reached from here; never a second one.
 *
 * ── By event, as served (batch 11 R8a, COA-D7) ────────────────────────────────────────────────────
 * The server groups the rows — recognition, settlement, adjustment — and nests each reversal under
 * the entry it takes back; this panel draws a heading where the role changes and indents a
 * reversal with its reason. A row reached through ANOTHER document carries `via` (its number opens
 * that document through sourceDoc.tsx SourceDocOpen); a row shown only for context (a goods
 * receipt's tax invoice, a payment's bills) is folded until asked for. ⛔ Rendered AS SERVED: no
 * filter, no sort, no regrouping here (W2-26) — the order is the resolver's.
 *
 * ── The empty answers ───────────────────────────────────────────────────────────────────────────
 * FIVE of them (`blank.code`), each its own sentence, never one blank panel for all: not generated
 * yet (with the generator's own reason per source, and the way to Generate), waiting (for a receipt,
 * a Book, a statement line, a pairing), recorded elsewhere (with the document it is on), never posts
 * by design, and no posting rule yet. The blank is about the document's OWN event, so it can stand
 * above rows reached through a relation (a service invoice: "recorded on RE-…", and the receipt's
 * entries below). An older server sends no blank: then `postsEntries` picks between the two
 * sentences it always had. The server's own `why` rides along as the hover text, so its exact words
 * are never lost.
 */

/**
 * ⛔ THE CLIENT ID IS ALL THIS PANEL EVER NEEDED, and saying so in the type is what lets the ledger
 * open a document (batch 6, COA-D10): a voucher's source document is read under the client the
 * SERVER says the voucher belongs to (`clientId` of GET /journal/entries/:entryId), and there is no
 * shell `AwCtx` at that point to hand over. Narrowed rather than casting a half-built ctx: the
 * period, the reload counter and the nav are not read here, so demanding them was a lie about this
 * panel's dependencies — and the panes that call it still pass their whole ctx unchanged.
 */
export type DocCtx = Pick<AwCtx, 'clientId'>;

/** One row of GET /journal/entries-for/:kind/:id. */
export type DocEntryRow = {
  id: string; entryDate: string; book: string; docNo: string; narration: string;
  status: string; postedAt: number | null; pageNo: number | null;
  /** SATANG, the ledger's own unit — Σ dr of this ONE entry, never a running total. */
  amount: number;
  /** This entry undoes another one. The route derives it from reverses_entry_id. */
  isReversal: boolean;
  reversedBy: { docNo: string; status: string; entryId: string } | null;
  /** Why a reversal was raised (batch 11 R2) — the person's own words, '' on an entry that is not a reversal. A
   *  payroll reversal's comes from the payroll row that raised it. Optional: an older server sends none. */
  reason?: string;
  // ── Batch 11 R8a (services/journalLinks.ts DocEntryRowV2). Every one optional, so an older server still renders. ──
  /** What the entry did for this document: recognised it, settled it, adjusted it — or took one of those back. */
  role?: 'recognition' | 'settlement' | 'adjustment' | 'reversal' | '';
  /** The document the entry was reached through (a receipt, a note, a payment, a bill) — null on the document's own. */
  via?: { kind: string; id: string; docNo: string } | null;
  /** Shown for context only (a goods receipt's tax invoice, a payment's bills) — folded until asked for. */
  context?: boolean;
  /** The entry this one reverses ('' when it is not a reversal). */
  reversesEntryId?: string;
  /** On a draft: why POST /journal/:id/post would refuse it (journalPost.postRefusalOf), null when it would not. */
  postRefusal?: { code: string; message: string; reason?: string; words?: { key: string; params: Record<string, string> } } | null;
  /** On a posted entry: why POST /journal/:id/reverse would refuse it, null when it would not. */
  reverseRefusal?: { code: string; message: string } | null;
  /** On a draft: its source has moved since it was generated. */
  stale?: boolean;
  /** A manual entry made by hand FOR the source it is listed under (W1's Record by hand): it holds that source. */
  byHand?: boolean;
  /** (W2 ship) …and its source no longer says what it recorded (wave 1's b5a3c375): said on the row, never refused. */
  handChanged?: { kind: string; label: string; status: string } | null;
};
/** Why a document's own event has no entry — one of five (services/journalLinks.ts DocBlank). */
export type DocBlank = {
  code: 'not_generated' | 'waiting' | 'elsewhere' | 'by_design' | 'no_rule';
  why: string; params: Record<string, string>;
  /** (W4 R7) `cash_line`: a petty cash row, with its box (`boxId`) — the Generate link opens Banking › Petty Cash on it. */
  sources?: { kind: 'tax_line' | 'bank_line' | 'cash_line'; id: string; docNo: string; why: string; boxId?: string }[];
  goTo?: { kind: string; id: string; docNo: string } | null;
  /** (W2 ship) no_rule on a statement line only Record by hand can book: the source the "Record by hand" door opens for.
   *  (W4-16) …and a not-generated line or petty cash row of a client not registered for VAT (client_not_vat). */
  byHand?: { kind: 'bank_line' | 'cash_line'; id: string; label: string } | null;
};
export type DocRelated = { kind: string; id: string; docNo: string; role: 'recognition' | 'settlement' | 'adjustment' | 'reversal'; blank: DocBlank | null };
export type DocEntriesAnswer = { rows: DocEntryRow[]; postsEntries: boolean; why: string; related?: DocRelated[]; blank?: DocBlank | null };

const STATUS_KEY: Record<string, string> = {
  posted: 'aw_jl_status_posted', draft: 'aw_jl_status_draft',
};
const statusPill = (s: string): React.CSSProperties => s === 'posted' ? pillGreen : s === 'draft' ? pillAmber : pillGrey;

/** The group headings, by the role the server gave (a reversal stays inside the group of what it reverses). */
const ROLE_KEY: Record<string, string> = {
  recognition: 'aw_ef_role_recognition', settlement: 'aw_ef_role_settlement', adjustment: 'aw_ef_role_adjustment',
};
/** The five blanks; `waiting` is worded by its case. ⛔ Every key written in full, so a grep finds each. */
const BLANK_KEY: Record<string, string> = {
  not_generated: 'aw_ef_blank_not_generated', waiting: 'aw_ef_blank_waiting', elsewhere: 'aw_ef_blank_elsewhere',
  by_design: 'aw_ef_blank_by_design', no_rule: 'aw_ef_blank_no_rule',
};
/** no_rule, worded by its case (W2 ship): never "enter a manual voucher" — an entry linked to nothing would stand beside
 *  the one Generate derives once the rule exists or the line is matched. ⛔ Every key written in full. */
const NO_RULE_KEY: Record<string, string> = {
  transfer: 'aw_ef_blank_no_rule_transfer', reimbursement: 'aw_ef_blank_no_rule_reimbursement', ignored: 'aw_ef_blank_no_rule_ignored',
  quotation: 'aw_ef_blank_no_rule_quotation', not_vat: 'aw_ef_blank_no_rule_not_vat',
};
/** A blank's sentence key: waiting and no_rule by their case, the rest by code. */
const blankKey = (b: DocBlank): string => (b.code === 'waiting' && WAITING_KEY[b.params?.case || ''] ? WAITING_KEY[b.params.case]
  : b.code === 'no_rule' && NO_RULE_KEY[b.params?.case || ''] ? NO_RULE_KEY[b.params.case] : BLANK_KEY[b.code]);
const WAITING_KEY: Record<string, string> = {
  unreceipted: 'aw_ef_blank_waiting_unreceipted', not_booked: 'aw_ef_blank_waiting_not_booked', no_line: 'aw_ef_blank_waiting_no_line',
  unpaired: 'aw_ef_blank_waiting_unpaired', unmatched: 'aw_ef_blank_waiting_unmatched', note_not_booked: 'aw_ef_blank_waiting_note_not_booked',
  no_vat_line: 'aw_ef_blank_waiting_no_vat_line',
};
/** How a hand entry's source is named (Manual entries' own keys, pa_manual_for_*). ⛔ Every key written in full. */
const HAND_FOR_KEY: Record<string, string> = {
  tax_line: 'pa_manual_for_tax_line', bank_line: 'pa_manual_for_bank_line', cash_line: 'pa_manual_for_cash_line',
};
/** postEntry's refusals, in journalPost.POST_REFUSALS' order (testSettleI18n holds the two lists equal). A code this
 *  file does not know is said in the server's own words. */
export const POST_REF_KEY: Record<string, string> = {
  not_found: 'aw_ef_postref_not_found', payroll: 'aw_ef_postref_payroll', already_posted: 'aw_ef_postref_already_posted',
  period_closed: 'aw_ef_postref_period_closed', no_lines: 'aw_ef_postref_no_lines', unbalanced: 'aw_ef_postref_unbalanced',
  book_unknown: 'aw_ef_postref_book_unknown', bank_account_missing: 'aw_ef_postref_bank_account_missing', stale: 'aw_ef_postref_stale',
  already_in_books: 'aw_ef_postref_already_in_books', recorded_by_hand: 'aw_ef_postref_recorded_by_hand',
  hand_source_gone: 'aw_ef_postref_hand_source_gone', hand_source_held: 'aw_ef_postref_hand_source_held',
  hand_prepaid_taken: 'aw_ef_postref_hand_prepaid_taken',
  unbacked: 'aw_ef_postref_unbacked', account_missing: 'aw_ef_postref_account_missing',
  account_gate: 'aw_ef_postref_account_gate', asset_reversal: 'aw_ef_postref_asset_reversal', reversal_of_released: 'aw_ef_postref_reversal_of_released',
  reversal_not_mirror: 'aw_ef_postref_reversal_not_mirror',
};
/** (W3.1) A stale draft's refusal worded by its staleness reason (journalPost.postRefusalOf carries it) — the drafts card's
 *  own sentences: "post that one first" (prepaid_wait) is not "discard it and generate again". A reason this file does not
 *  know falls back to aw_ef_postref_stale. ⛔ Every key written in full. */
const STALE_KEY: Record<string, string> = {
  line: 'pa_draft_stale_r_line', items: 'pa_draft_stale_r_items', both: 'pa_draft_stale_r_both', gone: 'pa_draft_stale_r_gone',
  vouchers: 'pa_draft_stale_r_vouchers', prepaid: 'pa_draft_stale_r_prepaid', prepaid_wait: 'pa_draft_stale_r_prepaid_wait',
};
/** The refusals a POSTED row can meet (manualEntry.REVERSE_REFUSALS less not_found / not_posted, plus
 *  source_period_closed). Five are Manual entries' own words (pa_manual_rev_ref_*, which take {{docNo}}). */
export const REV_REF_KEY: Record<string, string> = {
  payroll: 'aw_ef_revref_payroll', already_reversed: 'aw_ef_revref_already_reversed',
  asset: 'pa_manual_rev_ref_asset', asset_reversal: 'pa_manual_rev_ref_asset_reversal',
  reversal_of_released: 'pa_manual_rev_ref_reversal_of_released', no_open_period: 'pa_manual_rev_ref_no_open_period',
  no_lines: 'pa_manual_rev_ref_no_lines', source_period_closed: 'aw_ef_revref_source_period_closed',
};

/** The book's own statutory name where the country pack prescribes one, else the stored key.
 *  ⛔ Never a name composed here — statutoryBooks is the country pack's (services/countryPacks.ts),
 *  the same source services/ledgerBooks.ts prints on the cover of the volume. */
const journalName = (book: string): string => statutoryBookName(book) || book;

/** A document's number, opening that document (sourceDoc.tsx — the one dispatcher, three answers). */
function DocLink({ ctx, d }: { ctx: DocCtx; d: { kind: string; id: string; docNo: string } }) {
  return <SourceDocOpen clientId={ctx.clientId} s={{ kind: d.kind, id: d.id, docNo: d.docNo, openable: true, why: '' }} label={d.docNo} />;
}

/** The way on from "not generated": the place where Generate settles THIS source (W2-27; batch 11 W3 R6, W3-21) — a place,
 *  never a write from here. A statement line's settlement is generated in Banking › Reconciliation (its Generate settles
 *  the period's matched lines); Accounting › Journal entries' Generate derives the tax lines' entries only, so a
 *  statement line sent there met a Generate that never settles it. Every other source keeps Journal entries. It LEAVES
 *  (the pane underneath unmounts), so through guardLeave, one replacing URL write, as sourceDoc.tsx "Open in Expenses"
 *  does. Outside a router (a bare test mount), or for a read-only seat, it is said in words — split the same way. */
// (Batch 11 W4 R7, P7b task 7) A petty cash row (`cash_line`) is settled from Banking › Petty Cash — its row's "Create draft
// entry", opened on the row's box (?box=<id>, which the pane reads once and takes off the URL); Reconciliation's Generate
// takes the period's rows too, but the box is where the row is seen.
function GenerateLink({ kind, boxId }: { kind: string; boxId?: string }) {
  const { t } = useTranslation();
  const inRouter = useInRouterContext();
  const readOnly = useReadOnlySeat();
  const to = kind === 'cash_line' ? 'petty_cash' : kind === 'bank_line' ? 'banking' : 'accounting';
  const whereKey = to === 'petty_cash' ? 'aw_ef_generate_where_cash' : to === 'banking' ? 'aw_ef_generate_where_bank' : 'aw_ef_generate_where';
  if (!inRouter || readOnly) return <span style={note} data-ef-generate="where" data-ef-generate-to={to}>{t(whereKey)}</span>;
  return <GenerateLinkRouted to={to} boxId={boxId || ''} />;
}
function GenerateLinkRouted({ to, boxId }: { to: 'petty_cash' | 'banking' | 'accounting'; boxId: string }) {
  const { t } = useTranslation();
  const [, setParams] = useSearchParams();
  const go = () => guardLeave(() => {
    setParams(prev => {
      const n = new URLSearchParams(prev);
      if (to === 'petty_cash') { n.set('menu', 'banking'); n.set('sub', 'petty_cash'); if (boxId) n.set('box', boxId); else n.delete('box'); }
      else if (to === 'banking') { n.set('menu', 'banking'); n.set('sub', 'reconciliation'); }
      else { n.set('menu', 'accounting'); n.set('sub', 'journal_entries'); }
      return n;
    }, { replace: true });
  });
  const linkKey = to === 'petty_cash' ? 'aw_ef_generate_link_cash' : to === 'banking' ? 'aw_ef_generate_link_bank' : 'aw_ef_generate_link';
  return (
    <button type="button" onClick={go} data-ef-generate="go" data-ef-generate-to={to}
      style={{ ...btn, padding: '4px 10px', fontSize: 12, borderColor: 'var(--accent)', color: 'var(--accent-text)' }}>
      <i className="ti ti-arrow-right" aria-hidden /> {t(linkKey)}
    </button>
  );
}

/** (W2 ship) "Record by hand" on a no_rule statement line: Accounting › Journal entries, its New form made FOR the line
 *  (journal_entries.hand_kind / hand_id — wave 1's door), which holds it, so nothing books it a second time once it is
 *  matched. Staff who write the books only; a read-only seat (or a host with no router) reads the sentence alone. */
function RecordByHandLink({ src }: { src: { kind: string; id: string; label: string } }) {
  const inRouter = useInRouterContext();
  const readOnly = useReadOnlySeat();
  if (!inRouter || readOnly) return null;
  return <RecordByHandRouted src={src} />;
}
function RecordByHandRouted({ src }: { src: { kind: string; id: string; label: string } }) {
  const { t } = useTranslation();
  const [, setParams] = useSearchParams();
  const go = () => guardLeave(() => {
    setParams(prev => {
      const n = new URLSearchParams(prev);
      n.set('menu', 'accounting'); n.set('sub', 'journal_entries');
      n.set('forKind', src.kind); n.set('forId', src.id); n.set('forLabel', src.label);
      return n;
    }, { replace: true });
  });
  return (
    <button type="button" onClick={go} data-ef-by-hand-go={src.id}
      style={{ ...btn, padding: '4px 10px', fontSize: 12, borderColor: 'var(--accent)', color: 'var(--accent-text)' }}>
      <i className="ti ti-pencil" aria-hidden /> {t('pa_manual_by_hand_btn')}
    </button>
  );
}

/** Why the document's own event has no entry — the blank's sentence, its way on, and the server's words on hover. */
function BlankLine({ ctx, b }: { ctx: DocCtx; b: DocBlank }) {
  const { t } = useTranslation();
  // (W2 ship, a low) The elsewhere sentence beside its link names the document once — the link is the number.
  const key = b.code === 'elsewhere' && b.goTo ? 'aw_ef_blank_elsewhere_link' : blankKey(b);
  return (
    <div style={{ ...card, padding: '9px 12px', display: 'flex', flexDirection: 'column', gap: 6 }} data-ef-blank={b.code} data-ef-blank-case={b.params?.case || undefined}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12.5 }} title={b.why || undefined}>
        <i className="ti ti-info-circle" style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 2 }} aria-hidden />
        <span>
          {key ? t(key, { docNo: b.goTo?.docNo || b.params?.docNo || '' }) : b.why}
          {b.code === 'elsewhere' && b.goTo && <> <DocLink ctx={ctx} d={b.goTo} /></>}
        </span>
      </div>
      {b.code === 'no_rule' && b.byHand && <div style={{ paddingLeft: 22 }}><RecordByHandLink src={b.byHand} /></div>}
      {b.code === 'not_generated' && (
        <>
          {(b.sources || []).map(x => (
            <div key={x.id} style={{ ...note, paddingLeft: 22 }} data-ef-source={x.id}>
              <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{x.docNo}</span>: {x.why || t('aw_ef_would_generate')}
            </div>
          ))}
          <div style={{ paddingLeft: 22 }}><GenerateLink kind={String(b.sources?.[0]?.kind || '')} boxId={String(b.sources?.[0]?.boxId || '')} /></div>
          {/* (W4-16) A client not registered for VAT: its settlement is recorded by hand, made FOR this source (it holds it). */}
          {b.byHand && <div style={{ paddingLeft: 22 }}><RecordByHandLink src={b.byHand} /></div>}
        </>
      )}
    </div>
  );
}

/** The entries one document produced, by event, with a way through to each voucher — and, for a seat that writes,
 *  Post on a draft and Reverse… on a posted entry, each only where the server would accept it. Exported for a host whose
 *  opener does not outlive the click (a row's ⋯ menu closes as it opens this, panes/Documents.tsx): the host holds it. */
export function DocEntriesDialog({ ctx, kind, id, docNo, focusEntryId, onClose }: {
  ctx: DocCtx; kind: string; id: string; docNo: React.ReactNode; focusEntryId?: string; onClose: () => void;
}) {
  const { t } = useTranslation();
  const readOnly = useReadOnlySeat();
  const [d, setD] = useState<DocEntriesAnswer | null>(null);
  const [err, setErr] = useState('');
  const [tick, setTick] = useState(0);              // a Post or a reversal re-reads the answer
  const [entryId, setEntryId] = useState('');     // the voucher handed over to the detail view
  const [showCtx, setShowCtx] = useState(false);
  const [busy, setBusy] = useState('');
  const [asking, setAsking] = useState<DocEntryRow | null>(null);
  // A refused reversal's words (W1's onRefused): kept across the re-read it triggers, cleared by the next write.
  const [refusedMsg, setRefusedMsg] = useState('');
  const focusRef = useRef<HTMLTableRowElement | null>(null);
  useEffect(() => {
    let alive = true;
    api.jnlEntriesFor(ctx.clientId, kind, id)
      .then((r: DocEntriesAnswer) => { if (alive) { setD(r); setErr(''); } })
      .catch((e: any) => { if (alive) setErr(String(e?.message || e) || t('aw_ef_failed')); });
    return () => { alive = false; };
  }, [ctx.clientId, kind, id, tick]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Opened on one entry (a refusal's "Reverse JV-…"): that row is brought into view once the answer is in.
  useEffect(() => {
    const el: any = focusRef.current;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center' });
  }, [d, focusEntryId]);

  const rows = d?.rows || [];
  const related = d?.related || [];
  // A reversal in the list is the one thing a reader must not misread as a second posting.
  const reversed = rows.some(r => r.isReversal || !!r.reversedBy);
  let contextN = 0;
  for (const r of rows) if (r.context) contextN++;
  const post = async (r: DocEntryRow) => {
    if (busy) return;
    setBusy(r.id); setErr(''); setRefusedMsg('');
    try { await api.jnlPost(r.id); setTick(x => x + 1); }
    catch (e: any) {
      // (Batch 11 W4 P9, LB-28) Posted meanwhile by someone else (already_posted, naming who and when): done — read again,
      // no error box.
      if (e?.payload?.code === 'already_posted') setTick(x => x + 1);
      else setErr(String(e?.message || e));
    }
    finally { setBusy(''); }
  };
  const cols = readOnly ? 8 : 9;
  // One pass over the rows AS SERVED: a heading where the role changes (a reversal stays in its group), the
  // context rows folded unless asked for.
  const body: React.ReactNode[] = [];
  let group = '';
  for (const r of rows) {
    const g = r.role === 'reversal' ? group : (r.role || '');
    if (g && g !== group && ROLE_KEY[g]) {
      body.push(
        <tr key={`g-${r.id}`} data-ef-group={g}>
          <td colSpan={cols} style={{ ...td, background: 'var(--bg-app)', fontSize: 11, fontWeight: 800, letterSpacing: '.03em', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>
            {t(ROLE_KEY[g])}
          </td>
        </tr>,
      );
    }
    group = g;
    if (r.context && !showCtx) continue;
    const nested = r.role === 'reversal' || (!r.role && r.isReversal);
    const focused = !!focusEntryId && focusEntryId === r.id;
    body.push(
      <tr key={r.id} data-ef-row={r.id} data-ef-role={r.role || ''} ref={focused ? focusRef : undefined}
        style={{ ...(focused ? { outline: '2px solid var(--accent)', outlineOffset: -2 } : {}), ...(r.context ? { opacity: 0.75 } : {}) }}>
        <td style={{ ...td, whiteSpace: 'nowrap', ...(nested ? { paddingLeft: 26 } : {}) }}>
          {nested && <i className="ti ti-corner-down-right" style={{ color: 'var(--text-tertiary)', marginRight: 4 }} aria-hidden />}{dmy(r.entryDate)}
        </td>
        <td style={{ ...td, whiteSpace: 'nowrap' }}>
          {/* The hand-off: the voucher's own window (JournalBook.tsx EntryDetailDialog), which is where its lines
              and its debit/credit totals are shown. */}
          <button type="button" onClick={() => setEntryId(r.id)} title={t('aw_ef_open_entry')} data-ef-open={r.id}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 700, color: 'var(--accent)', fontSize: 12.5, fontVariantNumeric: 'tabular-nums' }}>
            {r.docNo}
          </button>
          {r.isReversal && <div><span style={pillGrey}>{t('aw_ef_reversal')}</span></div>}
          {/* A manual entry made for the source by hand (W1 ship): the source's own event, said as such. */}
          {r.byHand && <div><span style={pillGrey} title={t('aw_ef_by_hand_title')} data-ef-by-hand>{t('aw_ef_by_hand')}</span></div>}
          {/* (W2 ship, wave 1's b5a3c375) its source changed since it was made / posted: the drafts card's and the banner's
              own words, amber — Post and Reverse stay as the doors answer them. */}
          {r.byHand && r.handChanged && (
            <div data-ef-hand-changed={r.handChanged.status} style={{ ...note, color: '#92400E', fontWeight: 600, whiteSpace: 'normal', maxWidth: 320 }}>
              <i className="ti ti-alert-triangle" aria-hidden />{' '}
              {r.handChanged.status === 'draft'
                ? t('pa_draft_hand_changed', { docNo: r.docNo, what: HAND_FOR_KEY[r.handChanged.kind] ? t(HAND_FOR_KEY[r.handChanged.kind], { label: r.handChanged.label }) : r.handChanged.label })
                : t('aw_ib_hand_changed', { docNo: r.docNo })}
            </div>
          )}
          {/* Read where the reversal is listed: why the books were corrected (DBD ข้อ 4). */}
          {r.isReversal && !!r.reason && (
            <div style={{ ...note, whiteSpace: 'normal', maxWidth: 260 }} data-ef-reason>{t('aw_ef_reason', { reason: r.reason })}</div>
          )}
          {/* Reached through another document: its number, which opens it. */}
          {!!r.via && !nested && (
            <div style={{ ...note, whiteSpace: 'nowrap' }} data-ef-via={r.via.kind}>{t('aw_ef_via')} <DocLink ctx={ctx} d={r.via} /></div>
          )}
        </td>
        <td style={{ ...td, whiteSpace: 'nowrap' }}>{journalName(r.book)}</td>
        <td style={td}>{r.narration}</td>
        <td style={num}>{baht(r.amount)}</td>
        <td style={num}>{r.pageNo === null || r.pageNo === undefined ? '' : r.pageNo}</td>
        {/* The stored state, shown as stored: the three the route's own filter names have a word on the screen,
            anything else prints the value itself rather than being rounded down to "Posted". */}
        <td style={td}><span style={statusPill(r.status)}>{STATUS_KEY[r.status] ? t(STATUS_KEY[r.status]) : r.status}</span></td>
        <td style={{ ...td, whiteSpace: 'nowrap' }}>
          {r.reversedBy
            ? <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>{r.reversedBy.docNo}</span>
                <span style={statusPill(r.reversedBy.status)}>
                  {STATUS_KEY[r.reversedBy.status] ? t(STATUS_KEY[r.reversedBy.status]) : r.reversedBy.status}
                </span>
              </span>
            : ''}
        </td>
        {/* ⛔ The two writes, each only when the server's own refusal is null — otherwise the refusal, in words. A
            read-only seat has no column at all (W2-32). An older server sends neither field: nothing is offered. */}
        {!readOnly && (
          <td style={{ ...td, whiteSpace: 'normal', minWidth: 120 }}>
            {r.status !== 'posted'
              ? (r.postRefusal
                  ? <span style={note} title={r.postRefusal.message} data-ef-postref={r.postRefusal.code}>
                      <i className="ti ti-lock" aria-hidden /> {r.postRefusal.code === 'stale' && r.postRefusal.words
                        // (Batch 11 W4 P9, W3 lows) the vouchers named, the withholding's case — the drafts card's words.
                        ? t(r.postRefusal.words.key, { ...r.postRefusal.words.params, defaultValue: STALE_KEY[r.postRefusal.reason || ''] ? t(STALE_KEY[r.postRefusal.reason || '']) : r.postRefusal.message })
                        : r.postRefusal.code === 'stale' && STALE_KEY[r.postRefusal.reason || '']
                        ? t(STALE_KEY[r.postRefusal.reason || ''])
                        : POST_REF_KEY[r.postRefusal.code] ? t(POST_REF_KEY[r.postRefusal.code], { docNo: r.docNo }) : r.postRefusal.message}
                    </span>
                  : r.postRefusal === null && (
                    <button key="post" type="button" onClick={() => post(r)} disabled={!!busy} data-ef-post={r.id} style={{ ...actBtn, borderColor: 'var(--accent)', color: 'var(--accent-text)' }}>
                      <i className={busy === r.id ? 'ti ti-loader' : 'ti ti-check'} aria-hidden /> {t('aw_ef_post')}
                    </button>
                  ))
              : (r.reverseRefusal
                  ? <span style={note} title={r.reverseRefusal.message} data-ef-revref={r.reverseRefusal.code}>
                      <i className="ti ti-lock" aria-hidden /> {REV_REF_KEY[r.reverseRefusal.code] ? t(REV_REF_KEY[r.reverseRefusal.code], { docNo: r.docNo }) : r.reverseRefusal.message}
                    </span>
                  : r.reverseRefusal === null && (
                    <button key="reverse" type="button" onClick={() => { setRefusedMsg(''); setAsking(r); }} disabled={!!busy} data-ef-reverse={r.id} style={actBtn}>
                      <i className="ti ti-arrow-back-up" aria-hidden /> {t('aw_ef_reverse')}
                    </button>
                  ))}
          </td>
        )}
      </tr>,
    );
  }
  const blank = d?.blank || null;
  return (
    <Dialog title={t('aw_ef_title')} docNo={docNo} onClose={onClose} width={1000}
      footer={<div style={note}>{t('aw_ef_foot')}</div>}>
      {err && <div style={errBox}>{err}</div>}
      {refusedMsg && <div role="alert" style={errBox} data-ef-refused>{refusedMsg}</div>}
      {!d && !err ? <Loader /> : d && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {/* The document's OWN event, when it has no entry: one of five sentences, never one blank panel for all. */}
          {blank && <BlankLine ctx={ctx} b={blank} />}
          {/* An older server sends no blank: ONE line, two sentences, chosen by its own flag, its `why` on hover. */}
          {!blank && !rows.length && (
            <div style={emptyLine} title={d.why || undefined}>{t(d.postsEntries ? 'aw_ef_none' : 'aw_ef_never')}</div>
          )}
          {!!rows.length && (
            <>
              {contextN > 0 && (
                <div style={{ ...note, display: 'flex', gap: 8, alignItems: 'center' }} data-ef-context={contextN}>
                  <i className="ti ti-fold" aria-hidden /> {t('aw_ef_context', { n: contextN })}
                  <button type="button" onClick={() => setShowCtx(v => !v)} style={{ ...actBtn, padding: '2px 8px' }} data-ef-context-toggle>
                    {t(showCtx ? 'aw_ef_context_hide' : 'aw_ef_context_show')}
                  </button>
                </div>
              )}
              <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
                <table style={table}>
                  <thead>
                    <tr>
                      <th style={th}>{t('aw_col_date')}</th>
                      <th style={th}>{t('aw_jl_entry_no')}</th>
                      <th style={th}>{t('aw_ef_journal')}</th>
                      <th style={th}>{t('aw_jl_narration')}</th>
                      <th style={{ ...th, textAlign: 'right' }}>{t('aw_dd_amount')}</th>
                      <th style={{ ...th, textAlign: 'right' }}>{t('aw_jl_page_no')}</th>
                      <th style={th}>{t('aw_col_status')}</th>
                      <th style={th}>{t('aw_jl_reversed_by')}</th>
                      {!readOnly && <th style={th}>{t('aw_col_actions')}</th>}
                    </tr>
                  </thead>
                  <tbody>{body}</tbody>
                </table>
              </div>
              {/* ⛔ Said in words where it is read, not left to the pill: two rows that cancel out
                  are not two postings, and nothing on this screen adds them up. */}
              {reversed && (
                <div style={{ ...card, padding: '9px 12px', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <i className="ti ti-info-circle" style={{ color: 'var(--accent)', flexShrink: 0, marginTop: 1 }} aria-hidden />
                  <div style={note}>{t('aw_ef_reversal_note')}</div>
                </div>
              )}
            </>
          )}
          {/* The documents the relations reached, as served — each opens, and says its own blank when it has one. */}
          {!!related.length && (
            <div style={{ ...card, padding: '9px 12px', display: 'flex', flexDirection: 'column', gap: 5 }} data-ef-related={related.length}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.03em', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>{t('aw_ef_related')}</div>
              {related.map(x => (
                <div key={`${x.kind}-${x.id}`} style={{ fontSize: 12.5, display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'baseline' }} data-ef-rel={x.kind}>
                  <span style={pillGrey}>{ROLE_KEY[x.role] ? t(ROLE_KEY[x.role]) : x.role}</span>
                  <DocLink ctx={ctx} d={x} />
                  <span style={note} title={x.blank?.why || undefined}>
                    — {x.blank
                      ? t(blankKey(x.blank), { docNo: x.blank.goTo?.docNo || '' })
                      : t('aw_ef_related_listed')}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {/* The voucher itself — this round's other part, mounted rather than re-implemented. It is a
          Dialog inside a Dialog, which the shell carries by design: Esc answers the inner-most
          window by nesting depth (see utils/escLayer.ts). */}
      {entryId && <EntryDetailDialog entryId={entryId} onClose={() => setEntryId('')} />}
      {/* Reverse… — R2's dialog, which asks why and owns its own dirty guard; the reversal it raises is a DRAFT,
          listed under the entry it takes back with its own Post. A REFUSAL (the server's 4xx — the entry reversed
          meanwhile in another tab, its period closed since the panel read it) closes the dialog through W1's onRefused:
          the server's words stand on the panel, which reads its answer again, so a refused Reverse is never pressed twice. */}
      {asking && (
        <ReverseReasonDialog docNo={asking.docNo} line={t('aw_ef_reverse_line', { docNo: asking.docNo })}
          onClose={() => setAsking(null)}
          onSubmit={async reason => { await api.jnlReverse(asking.id, reason); setAsking(null); setTick(x => x + 1); }}
          onRefused={message => { setAsking(null); setRefusedMsg(message); setTick(x => x + 1); }} />
      )}
    </Dialog>
  );
}

/**
 * The entry point: one button, and the panel it opens.
 *
 * ⛔ Offered per KIND, uniformly on every row of that kind — never per row. Whether a particular
 * document has posted anything is the RESOLVER's answer, and a second copy of its criterion here
 * (merchant_payables.tax_line_id is right there on the row, so the temptation is real) would drift
 * from it and then HIDE a button on a document that does have entries. The panel's own sentence
 * says plainly when nothing has been posted yet, which is the honest answer and costs one click.
 */
export function DocEntriesBtn({ ctx, kind, id, docNo, style, label, focusEntryId }: {
  ctx: DocCtx;
  /** The document's STORED doc_type (or its family key) — passed to the server untouched. */
  kind: string;
  id: string;
  /** What the window is about, beside its title. The document's own number. */
  docNo: React.ReactNode;
  style?: React.CSSProperties;
  /** The button's face instead of the panel's name (batch 11 R8a: "Reverse JV-…" beside a refusal, an icon in a
   *  dense row). The panel's name stays the hover text. */
  label?: React.ReactNode;
  /** Open on this entry: its row is brought into view and outlined (batch 11 R8a — a refusal's "Reverse JV-…"). */
  focusEntryId?: string;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} title={label === undefined ? t('aw_ef_btn_title') : `${t('aw_ef_title')} — ${t('aw_ef_btn_title')}`}
        style={style || actBtn} data-ef-btn={kind}>
        {label === undefined ? <><i className="ti ti-book-2" aria-hidden /> {t('aw_ef_title')}</> : label}
      </button>
      {open && <DocEntriesDialog ctx={ctx} kind={kind} id={id} docNo={docNo} focusEntryId={focusEntryId} onClose={() => setOpen(false)} />}
    </>
  );
}
